import { BUILTIN_SOUNDS, ChannelSound } from "@/utils/notificationSounds";
import { playNotificationSound } from "@/utils/sound-utils";

function SoundTestButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="w-4 h-4"
      >
        <path d="M10 3.75a.75.75 0 0 0-1.264-.546L4.703 7H3.167a.75.75 0 0 0-.7.48A6.985 6.985 0 0 0 2 10c0 .887.165 1.737.468 2.52.111.29.39.48.7.48h1.535l4.033 3.796A.75.75 0 0 0 10 16.25V3.75ZM15.95 5.05a.75.75 0 0 0-1.06 1.061 5.5 5.5 0 0 1 0 7.778.75.75 0 0 0 1.06 1.06 7 7 0 0 0 0-9.899Z" />
        <path d="M13.829 7.172a.75.75 0 0 0-1.061 1.06 2.5 2.5 0 0 1 0 3.536.75.75 0 0 0 1.06 1.06 4 4 0 0 0 0-5.656Z" />
      </svg>
      Test
    </button>
  );
}

/**
 * Per-channel notification sound + volume control: a sound dropdown over the
 * built-in library, a volume slider, and a Test button that previews the
 * current selection. Used once per channel in the Notifications settings tab.
 */
export default function ChannelSoundPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: ChannelSound;
  onChange: (patch: Partial<ChannelSound>) => void;
  disabled?: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-3 ${
        disabled ? "pointer-events-none opacity-40" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        <select
          value={value.soundId}
          onChange={(e) => onChange({ soundId: e.target.value })}
          className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-squawk-blue focus:outline-none"
        >
          {BUILTIN_SOUNDS.map((sound) => (
            <option key={sound.id} value={sound.id} className="bg-gray-900">
              {sound.label}
            </option>
          ))}
        </select>
        <SoundTestButton
          onClick={() =>
            playNotificationSound({
              soundId: value.soundId,
              volume: value.volume,
            })
          }
        />
      </div>
      <div className="flex items-center gap-3">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-4 h-4 shrink-0 text-gray-400"
        >
          <path d="M10 3.75a.75.75 0 0 0-1.264-.546L4.703 7H3.167a.75.75 0 0 0-.7.48A6.985 6.985 0 0 0 2 10c0 .887.165 1.737.468 2.52.111.29.39.48.7.48h1.535l4.033 3.796A.75.75 0 0 0 10 16.25V3.75ZM15.95 5.05a.75.75 0 0 0-1.06 1.061 5.5 5.5 0 0 1 0 7.778.75.75 0 0 0 1.06 1.06 7 7 0 0 0 0-9.899Z" />
        </svg>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={value.volume}
          onChange={(e) => onChange({ volume: Number(e.target.value) })}
          className="h-1 w-40 cursor-pointer appearance-none rounded-full bg-gray-600 accent-squawk-blue"
          aria-label="Notification volume"
        />
        <span className="w-10 text-right text-xs text-gray-400">
          {Math.round(value.volume * 100)}%
        </span>
      </div>
    </div>
  );
}
